import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import {
  MESSAGE_CODES,
  showErrorToast,
} from "../../../services/toast";

import FilePreviewModal from "./FilePreviewer";
const tSystem = (key, options) =>
  i18n.t(key, {
    ns: "System",
    ...(options && typeof options === "object" ? options : {}),
  });


const ZIP_MAX_SIZE = 500 * 1024 * 1024;
const MULTI_MAX_SIZE = 3 * 1024 * 1024;
const MULTI_MAX_FILES = 5;

// ────────────────────────────────────────────────────────
// ZipDropzone
// ────────────────────────────────────────────────────────
const ZipDropzone = ({ files, setFiles, t }) => {
  const { getRootProps, isDragActive } = useDropzone({
    maxSize: ZIP_MAX_SIZE,
    multiple: false,
    noClick: true,
    accept: {
      "application/zip": [".zip"],
      "application/x-rar-compressed": [".rar"],
    },
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const tooLarge = fileRejections.some((r) =>
          r.errors.some((e) => e.code === "file-too-large"),
        );
        showErrorToast({
          i18nKey: tooLarge
            ? MESSAGE_CODES.FILE_SIZE_EXCEEDED
            : MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
          i18nOptions: tooLarge
            ? { size: ZIP_MAX_SIZE / (1024 * 1024) }
            : { types: ".zip, .rar" },
          fallback: tooLarge
            ? tSystem(
                `This file exceeds ${ZIP_MAX_SIZE / (1024 * 1024)}MB.`,
              )
            : tSystem("Please upload files in .zip or .rar format."),
        });
        return;
      }
      if (acceptedFiles[0]) setFiles([acceptedFiles[0]]);
    },
  });

  const handleFileSelect = (event) => {
    const f = event.target.files[0];
    if (!f) return;
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (![".zip", ".rar"].includes(ext)) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
        i18nOptions: { types: ".zip, .rar" },
        fallback: tSystem("Only .zip or .rar files are accepted."),
      });
      return;
    }
    if (f.size > ZIP_MAX_SIZE) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
        i18nOptions: { size: ZIP_MAX_SIZE / (1024 * 1024) },
        fallback: tSystem(
          `This file exceeds ${ZIP_MAX_SIZE / (1024 * 1024)}MB.`,
        ),
      });
      return;
    }
    setFiles([f]);
    event.target.value = "";
  };

  return (
    <Box>
      <Typography sx={{ marginBottom: 1 }}>
        {t("Upload Supporting Document Single File Only")}
        <br />
        <span style={{ color: "grey" }}>{t("Maximum Size 500MB")}</span>
      </Typography>
      <Paper
        {...getRootProps()}
        sx={{
          border: "2px dashed gray",
          borderRadius: 2,
          padding: 3,
          textAlign: "center",
          cursor: "default",
          bgcolor: isDragActive ? "lightblue" : "background.paper",
        }}
      >
        <FolderZipIcon color="warning" sx={{ fontSize: "56px" }} />
        <Typography variant="body1">
          {isDragActive ? t("Release the file to upload") : t("Choose a file")}{" "}
          (.zip or .rar)
        </Typography>
        <Button
          variant="contained"
          component="label"
          sx={{ ml: 2, mt: 1, textTransform: "none" }}
        >
          {t("Select File")}
          <input
            type="file"
            accept=".zip,.rar"
            onChange={handleFileSelect}
            hidden
          />
        </Button>
      </Paper>

      {files.length > 0 && (
        <List sx={{ mt: 2, bgcolor: "background.paper", borderRadius: 2 }}>
          {files.map((file, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <Tooltip title={t("Delete")} arrow>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => setFiles([])}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FolderZipIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                secondary={`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

// ────────────────────────────────────────────────────────
// ListLeft
// ────────────────────────────────────────────────────────
const ListLeft = ({ files, setFiles, onPreview, t }) => {
  const { getRootProps, isDragActive } = useDropzone({
    maxSize: MULTI_MAX_SIZE,
    noClick: true,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    onDrop: (acceptedFiles, fileRejections) => {
      if (files.length >= MULTI_MAX_FILES) {
        showErrorToast({
          i18nKey: MESSAGE_CODES.FILE_MAX_COUNT_EXCEEDED,
          i18nOptions: { count: MULTI_MAX_FILES },
          fallback: tSystem(
            `You can upload a maximum of ${MULTI_MAX_FILES} files in total.`,
          ),
        });
        return;
      }
      if (fileRejections.length > 0) {
        const names = fileRejections.map((r) => r.file.name);
        if (names.some((n) => n.endsWith(".zip") || n.endsWith(".rar"))) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
            fallback: tSystem("Zip and Rar files are not allowed."),
          });
        } else if (
          fileRejections.some((r) =>
            r.errors.some((e) => e.code === "file-invalid-type"),
          )
        ) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
            fallback: tSystem(
              "Unsupported file type. Only PDF, XLSX, DOCX and image files are allowed.",
            ),
          });
        } else {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
            i18nOptions: { size: MULTI_MAX_SIZE / (1024 * 1024) },
            fallback: tSystem(
              `The selected file exceeds ${MULTI_MAX_SIZE / (1024 * 1024)}MB.`,
            ),
          });
        }
      }
      const remaining = MULTI_MAX_FILES - files.length;
      setFiles((prev) => [...prev, ...acceptedFiles.slice(0, remaining)]);
    },
  });

  const handleFileSelect = (event) => {
    const selected = Array.from(event.target.files);
    if (files.length + selected.length > MULTI_MAX_FILES) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_MAX_COUNT_EXCEEDED,
        i18nOptions: { count: MULTI_MAX_FILES },
        fallback: tSystem(
          `You can upload a maximum of ${MULTI_MAX_FILES} files in total.`,
        ),
      });
      event.target.value = "";
      return;
    }
    const valid = selected.filter((f) => f.size <= MULTI_MAX_SIZE);
    const rejected = selected.filter((f) => f.size > MULTI_MAX_SIZE);
    if (rejected.length > 0) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
        i18nOptions: { size: MULTI_MAX_SIZE / (1024 * 1024) },
        fallback: tSystem(
          `The selected file exceeds ${MULTI_MAX_SIZE / (1024 * 1024)}MB.`,
        ),
      });
    }
    setFiles((prev) => [...prev, ...valid]);
    event.target.value = "";
  };

  return (
    <>
      {/* 左：已選檔案列表 */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography>
          {t("Uploaded file list") + " "}
          <span style={{ color: "grey" }}>({t("up to 5 files allowed")})</span>
        </Typography>
        {files.length === 0 && (
          <Typography
            sx={{ color: "#aaa", fontSize: "14px", mt: 3, textAlign: "center" }}
          >
            {t("No files attached yet.")}
          </Typography>
        )}
        {files.length > 0 && (
          <List sx={{ bgcolor: "background.paper", borderRadius: 2 }} dense>
            {files.map((file, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <>
                    <Tooltip title={t("Preview")} arrow>
                      <IconButton
                        edge="end"
                        color="primary"
                        onClick={() => onPreview(file)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Delete")} arrow>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() =>
                          setFiles((prev) =>
                            prev.filter((f) => f.name !== file.name),
                          )
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <InsertDriveFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Grid>

      {/* 右：Dropzone */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ borderLeft: "2px solid #ccc", height: "100%" }}
      >
        <Box sx={{ m: 1 }}>
          <Typography sx={{ marginBottom: 1 }}>
            {t("Upload Supporting Documents")}
            <br />
            <span style={{ color: "grey" }}>
              {t("Maximum Size Per File")} : 3MB
            </span>
          </Typography>
          <Paper
            {...getRootProps()}
            sx={{
              border: "2px dashed gray",
              borderRadius: 2,
              padding: 3,
              textAlign: "center",
              cursor:
                files.length >= MULTI_MAX_FILES ? "not-allowed" : "default",
              bgcolor: isDragActive ? "lightblue" : "background.paper",
              opacity: files.length >= MULTI_MAX_FILES ? 0.5 : 1,
            }}
          >
            <CloudUploadIcon
              color={files.length >= MULTI_MAX_FILES ? "disabled" : "primary"}
              sx={{ fontSize: "56px" }}
            />
            <Typography variant="body1">
              {isDragActive
                ? t("Release the files or folders to upload")
                : t("Choose a file")}
              <br />
              (.xlsx, .pdf, .docx, image/*)
            </Typography>
            <Button
              variant="contained"
              component="label"
              disabled={files.length >= MULTI_MAX_FILES}
              sx={{ mt: 1, textTransform: "none" }}
            >
              {t("Select File")}
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                hidden
                accept=".pdf,.xlsx,.docx,.jpg,.png,.jpeg,.gif"
              />
            </Button>
          </Paper>
          {files.length >= MULTI_MAX_FILES && (
            <Typography
              sx={{ color: "red", fontSize: "14px", fontWeight: "bold", mt: 2 }}
            >
              {t("You have already selected 5 files.")}
            </Typography>
          )}
        </Box>
      </Grid>
    </>
  );
};

// ────────────────────────────────────────────────────────
// AddCustomEFAttachModal — 主元件
// ────────────────────────────────────────────────────────
const AddCustomEFAttachModal = ({
  open,
  onClose,
  onConfirm,
  initialFiles = [],
  initialMode = 1,
  efName = "",
}) => {
  const { t } = useTranslation("ActivityMain");
  const [selectedOption, setSelectedOption] = useState(initialMode);
  const [files, setFiles] = useState([...initialFiles]);

  // Preview state
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const disableMulti = files.length > 0 && selectedOption === 0;
  const disableZip = files.length > 0 && selectedOption === 1;

  useEffect(() => {
    if (open) {
      setFiles([...initialFiles]);
      setSelectedOption(initialMode);
    }
  }, [open]); // eslint-disable-line

  const handleChange = (event) => {
    setSelectedOption(Number(event.target.value));
    setFiles([]);
  };

  const handleSave = () => {
    if (onConfirm) onConfirm(files, selectedOption);
    onClose();
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        sx={{ zIndex: 10000 }}
        PaperProps={{
          sx: {
            height: "550px",
            width: selectedOption === 0 ? "500px" : "800px",
          },
        }}
      >
        <DialogTitle>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 10,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          {t("Upload Consumption Data For") + (efName ? " " + efName : "")}
        </DialogTitle>

        <FormControl sx={{ px: 3 }}>
          <RadioGroup row value={selectedOption} onChange={handleChange}>
            <FormControlLabel
              value={1}
              control={<Radio />}
              label={t("Upload multiple files")}
              sx={{ marginRight: 15 }}
              disabled={disableMulti}
            />
            <FormControlLabel
              value={0}
              control={<Radio />}
              label={t("Upload zip file")}
              disabled={disableZip}
            />
          </RadioGroup>
        </FormControl>

        <DialogContent sx={{ overflow: "hidden" }}>
          {selectedOption === 0 ? (
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <Grid size={12}>
                <ZipDropzone files={files} setFiles={setFiles} t={t} />
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              spacing={2}
              justifyContent="space-around"
              alignItems="flex-start"
              height="100%"
            >
              <ListLeft
                files={files}
                setFiles={setFiles}
                onPreview={handlePreview}
                t={t}
              />
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
          >
            {t("Close")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ textTransform: "none" }}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 內嵌預覽 Modal */}
      <FilePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        file={previewFile}
      />
    </>
  );
};

export default AddCustomEFAttachModal;
